import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {TranslateService} from '@ngx-translate/core';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import {NotificationService} from '@services/notification/notification.service';
import {AdcSetting} from '@models/adminstration/gateway/gateway-configuration/adc-setting';
import {AdcValue} from '@models/adminstration/gateway/gateway-configuration/adc-value';
import {GatewayImportExportService} from '@services/administration/gateway/import-export/gateway-import-export.service';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {
  ADC_TYPE_OF_DATA,
  GATEWAY_CONFIG_DATATYPE,
  HYSTERESIS_RANGE_MAX,
  HYSTERESIS_RANGE_MIN
} from '@constants/administration/gateway/gateway-constants';
import {lastValueFrom} from 'rxjs';

@Component({
    selector: 'scitis-admin-gateway-adc',
    templateUrl: './gateway-adc.component.html',
    styleUrls: ['./gateway-adc.component.scss'],
    standalone: false
})
export class GatewayAdcComponent implements OnInit, OnChanges {

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  isAdminRole: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  isTechAdminRole: boolean = false;

  @Input()
  gatewaySignals: GatewaySignals[] = new Array<GatewaySignals>();

  @Output()
  pushToIoTCoreEvent = new EventEmitter<any>();

  @Output()
  saveConfigurationEvent = new EventEmitter<any>();

  @ViewChild('uploader', {static: true}) input: ElementRef | undefined;

  isAdminOrSuperAdminOrTechAdmin: boolean = false;
  adcForm: UntypedFormGroup = new UntypedFormGroup({});
  settings: AdcSetting = new AdcSetting();
  MAX_NUMBER_OF_SIGNALS = 8;
  availableIndices: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  allowedIndices: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  name: string = '';
  jsonType: string = '';
  formGenerated: boolean = false;

  private SAMPLE_RATE_MIN = 100;
  private SAMPLE_RATE_MAX = 60000;

  DEFAULT_IO_TYPE = 'ANALOG';
  IO_TYPE_DIGITAL = 'DIGITAL';
  private DEFAULT_ADC_SIGNAL_NAME = 'adc_signal_';
  private COMPONENT_NAME = 'GatewayAdcComponent';

  subFormIsValid: boolean = true;

  private duplicateIndexMessage: string = '';
  private indexNotInRangeMessage: string = '';
  private noIndexMessage: string = '';
  private overwriteMessage: string = '';
  private importDifOrOverwriteMessage: string = '';
  private importDifButton: string = '';
  private invalidSampleRateMessage: string = '';
  private invalidTypeOfDataMessage: string = '';

  private readonly warningTitle: string = '';
  private readonly errorTitle: string = '';
  private readonly closeButton: string = '';
  private readonly cancelButton: string = '';
  private readonly duplicatedSignalsMessage: string = '';
  private readonly duplicatedSignalsInImportMessage: string = '';
  private readonly maximumNumberReachedInImportMessage: string = '';
  private readonly noSignalsInImportMessage: string = '';
  private readonly undefinedConnectorMessage: string = '';
  private readonly undefinedConfigurationMessage: string = '';
  private readonly uniqueNamesMessage: string = '';
  private readonly importSuccessMessage: string = '';
  private readonly undefinedSampleRateMessage: string = '';
  private readonly overWriteAllButton: string = '';

  constructor(
      private translate: TranslateService,
      private notification: NotificationService,
      private importExportService: GatewayImportExportService,
      private confirmationDialogService: ConfirmationDialogService) {
    this.duplicateIndexMessage = this.translate.instant('Duplicate index found for signal %s');
    this.noIndexMessage = this.translate.instant('The signal %s doesn\'t have an index');
    this.warningTitle = this.translate.instant('Warning');
    this.closeButton = this.translate.instant('Close');
    this.overWriteAllButton = this.translate.instant('Import all');
    this.importDifButton = this.translate.instant('Import first %1');
    this.overwriteMessage = this.translate.instant('The number of signals to be imported exceeds the maximum allowed of %1. Overwrite existing signals and import all ?');
    this.importDifOrOverwriteMessage = this.translate.instant('The number of signals to be imported exceeds the maximum allowed of %1. Import only first %2 or overwrite existing signals and import all?');
    this.duplicatedSignalsMessage = this.translate.instant('The following signals are duplicated');
    this.cancelButton = this.translate.instant('Cancel');
    this.errorTitle = this.translate.instant('Error');
    this.duplicatedSignalsInImportMessage = this.translate.instant('The ADC configuration has duplicated signals');
    this.maximumNumberReachedInImportMessage = this.translate.instant('The uploaded ADC configuration has more signals than maximum allowed of');
    this.noSignalsInImportMessage = this.translate.instant('The uploaded ADC configuration has no signals defined');
    this.invalidSampleRateMessage = this.translate.instant('The imported configuration sample rate "%1" is invalid. The value must be between %2 and %3 ms.');
    this.invalidTypeOfDataMessage = this.translate.instant('The imported configuration type of data %1 is different than the required one %2');
    this.undefinedConnectorMessage = this.translate.instant('The connector data type is undefined');
    this.undefinedConfigurationMessage = this.translate.instant('The configuration is undefined');
    this.uniqueNamesMessage = this.translate.instant('Please ensure all signal names are unique');
    this.importSuccessMessage = this.translate.instant('successfully imported configuration.');
    this.undefinedSampleRateMessage = this.translate.instant('The imported sample rate configuration is undefined');
    this.indexNotInRangeMessage = this.translate.instant('The index %1 of %2 is not in the range between 1 - 8');
  }

  get formControls() {
    return this.adcForm.controls;
  }

  ngOnInit() {
    this.initializeComponent();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.isAdminOrSuperAdminOrTechAdmin = this.isAdminRole || this.isSuperAdminRole || this.isTechAdminRole;
    this.setAdcSettings();
  }

  onFocusOut() {
    this.settings.sampleRateMs = this.formControls.sampleRate.value;
  }

  onFileSelected(event: Event) {
    const file = this.importExportService.onFileSelected(event);
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsText(file, 'UTF-8');
    fileReader.onload = async () => {
      if (!fileReader.result) {
        this.notification.error(this.undefinedConfigurationMessage, null, this.COMPONENT_NAME, true);
        return;
      }
      const importedConfig = JSON.parse(String(fileReader.result));
      const cloneActualAdcValues = [...this.settings.values];
      if (this.validateAdcConfig(importedConfig)) {
        const numberOfValuesToImport = await this.verifyCountOfSignals(importedConfig.values.length);
        if (numberOfValuesToImport === -1) {
          return;
        } else if (numberOfValuesToImport === importedConfig.values.length && (numberOfValuesToImport + this.settings.values.length) > this.MAX_NUMBER_OF_SIGNALS) {
          this.settings.values = [];
        } else if (importedConfig.values.length > numberOfValuesToImport) {
          importedConfig.values = importedConfig.values.slice(0, numberOfValuesToImport);
        }
        if (this.verifyIfSignalIndex(importedConfig.values)) {
          this.parseAndImportConfig(importedConfig, this.settings);
          this.updateAvailableIndices(importedConfig.values);
          this.notification.success(this.importSuccessMessage, this.COMPONENT_NAME, true);
        } else {
          this.settings.values = [...cloneActualAdcValues];
        }
      }
    };
    if (this.input) {
      this.input.nativeElement.value = '';
    }
  }

  onExportAdcConfig() {
    const filename = this.importExportService.createAdcFileName(this.gateway.id);
    const details = this.importExportService.generateAdcExportDetails(this.gateway.id, this.settings);

    const hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/json;charset=utf-8,' + encodeURI(details);
    hiddenElement.target = '_blank';
    hiddenElement.download = filename;
    hiddenElement.click();
  }

  save() {
    if (this.adcForm.invalid || !this.subFormIsValid) {
      return;
    }
    if (this.invalidSignalNamesExists()) {
      this.notification.warning(this.uniqueNamesMessage, this.COMPONENT_NAME);
      return;
    }
    if (!this.gatewaySignals || this.gatewaySignals.length < 1) {
      this.gatewaySignals = [new GatewaySignals()];
    }
    this.gatewaySignals[0]['@type'] = GATEWAY_CONFIG_DATATYPE;
    this.gatewaySignals[0].adc = this.settings;
    this.saveConfigurationEvent.emit(this.gatewaySignals[0]);
  }

  pushToIoTCore() {
    this.pushToIoTCoreEvent.emit();
  }

  addSignal() {
    if (!this.settings.values) {
      this.settings.values = [];
    }
    const index = this.availableIndices.shift();
    const adcValue: AdcValue = {
      hysteresis: HYSTERESIS_RANGE_MIN,
      index: index,
      ioType: this.DEFAULT_IO_TYPE,
      name: this.DEFAULT_ADC_SIGNAL_NAME + index,
      scaleMax: 0,
      scaleMin: 0
    };
    this.settings.values.push(adcValue);
    this.updateIndices();
  }

  removeSetting(adc: any) {
    const index = this.settings.values.indexOf(adc);
    if (index < 0) {
      return;
    }
    this.settings.values.splice(index, 1);
    this.addIndexToIndices(adc.index);
  }

  changeIndex(indexMap: Map<string, number>) {
    const newIndex = indexMap.get('new');
    const oldIndex = indexMap.get('old');
    if (newIndex !== undefined) {
      this.removeIndexFromIndices(newIndex);
    }
    if (oldIndex !== undefined) {
      this.addIndexToIndices(oldIndex);
    }
  }

  isAddSignalDisabled() {
    return this.settings.values.length >= this.MAX_NUMBER_OF_SIGNALS;
  }

  onCheckFormsValidation(formIsValid: boolean) {
    this.subFormIsValid = formIsValid;
  }

  buttonsDisabled(): boolean {
    return this.adcForm.invalid || !this.subFormIsValid || !this.isAdminOrSuperAdminOrTechAdmin;
  }

  private setAdcSettings() {
    if (!this.gatewaySignals || this.gatewaySignals.length < 1) {
      this.settings = new AdcSetting();
      this.settings.sampleRateMs = this.SAMPLE_RATE_MIN;
      this.removeUsedIndices();
      this.generateAdcForm(this.settings);
      return;
    }

    if (!this.gatewaySignals[0].adc) {
      this.settings = new AdcSetting();
    } else {
      this.settings = this.gatewaySignals[0].adc;
    }
    this.removeUsedIndices();
    this.generateAdcForm(this.settings);

  }

  private generateAdcForm(settings: AdcSetting) {
    this.adcForm = new UntypedFormGroup({
      sampleRate: new UntypedFormControl(settings.sampleRateMs, [Validators.required, Validators.min(this.SAMPLE_RATE_MIN), Validators.max(this.SAMPLE_RATE_MAX)])
    });
    this.formGenerated = true;
  }

  private removeUsedIndices() {
    if (!this.settings.values) {
      return;
    }
    for (const val of this.settings.values) {
      if (val.index !== undefined) {
        if (this.availableIndices.includes(val.index)) {
          this.availableIndices.splice(this.availableIndices.indexOf(val.index), 1);
        }
      }
    }
  }

  private addIndexToIndices(index: number) {
    this.availableIndices.push(index);
    this.availableIndices.sort((a, b) => a - b);
    this.updateIndices();
  }

  private removeIndexFromIndices(index: number) {
    const indexToRemove = this.availableIndices.indexOf(index);
    this.availableIndices.splice(indexToRemove, 1);
    this.updateIndices();
  }

  private invalidSignalNamesExists(): boolean {
    const signalNames: string[] = [];
    let hasInvalid = false;

    this.settings.values.forEach((element) => {
      if (signalNames.indexOf(element.name) !== -1) {
        hasInvalid = true;
      }
      signalNames.push(element.name);
    });
    return hasInvalid;
  }

  private updateIndices() {
    this.availableIndices = [...this.availableIndices];
  }

  private validateAdcConfig(newConfig: any) {
    if (!newConfig) {
      this.notification.error(this.undefinedConfigurationMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.typeOfData || newConfig.typeOfData.trim().length === 0) {
      this.notification.error(this.undefinedConnectorMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (newConfig.typeOfData !== ADC_TYPE_OF_DATA) {
      const message = this.invalidTypeOfDataMessage.replace('%1', newConfig.typeOfData).replace('%2', ADC_TYPE_OF_DATA);
      this.notification.error(message, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.sampleRateMs) {
      this.notification.error(this.undefinedSampleRateMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!this.isValidSampleRate(newConfig.sampleRateMs)) {
      const message = this.invalidSampleRateMessage.replace('%1', newConfig.sampleRateMs).replace('%2', String(this.SAMPLE_RATE_MIN)).replace('%3', String(this.SAMPLE_RATE_MAX));
      this.notification.error(message, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.values || newConfig.values.length === 0) {
      this.notification.error(this.noSignalsInImportMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (newConfig.values.length > this.MAX_NUMBER_OF_SIGNALS) {
      this.notification.error(this.maximumNumberReachedInImportMessage + ' ' + this.MAX_NUMBER_OF_SIGNALS, null, this.COMPONENT_NAME, true);
      return false;
    }
    const duplicates = this.getSignalDuplicates(newConfig);
    if (duplicates && duplicates.length > 0) {
      this.notification.error(this.duplicatedSignalsInImportMessage, null, this.COMPONENT_NAME, true);
      this.confirmationDialogService.openConfirmationDialog(this.errorTitle, duplicates, null, this.cancelButton);
      return false;
    }
    return true;
  }

  private isValidSampleRate(sampleRate: any): boolean {
    const number = Number(sampleRate);
    if (Number.isNaN(number)) {
      return false;
    }
    return !(number < this.SAMPLE_RATE_MIN || number > this.SAMPLE_RATE_MAX);
  }

  private getSignalDuplicates(newConfig: any) {
    const signalDuplicates = this.importExportService.getDuplicatesForConnectorConfig(newConfig, 'name');
    let result = '';
    const duplicatedSignals = this.importExportService.generateDuplicatesValueEnumeration(signalDuplicates);
    if (duplicatedSignals && duplicatedSignals.length > 0) {
      result += this.duplicatedSignalsMessage.toBold() + ': <br/>' + duplicatedSignals + '</br>';
    }
    return result.trim();
  }

  private parseAndImportConfig(newConfig: any, settings: AdcSetting) {
    this.formControls.sampleRate.setValue(newConfig.sampleRateMs);
    settings.sampleRateMs = newConfig.sampleRateMs;
    const newSignals = newConfig.values;
    const existingSignals = settings.values;
    for (const signal of newSignals) {
      let existingSignal = this.importExportService.findSignalByName(existingSignals, signal.name);
      if (!existingSignal) {
        existingSignals.push(this.updateSignalWithValidProperties(null, signal));
      } else {
        existingSignal = this.updateSignalWithValidProperties(existingSignal, signal);
      }
    }
    this.parseAndAdjustIndices(existingSignals);
  }

  private parseAndAdjustIndices(signals: AdcValue[]) {
    return signals.sort((s1, s2) => {
      const index1 = s1.index ?? 0;
      const index2 = s2.index ?? 0;
      if (index1 < index2) {
        return -1;
      }
      return (index1 > index2) ? 1 : 0;
    });
  }

  private async verifyCountOfSignals(numberOfNewValues: number) {
    const countValues = this.settings.values.length + numberOfNewValues;
    if (countValues > this.MAX_NUMBER_OF_SIGNALS) {
      const countOfValuesToImport = this.MAX_NUMBER_OF_SIGNALS - this.settings.values.length;
      let message: string, okButton: string, cancelButton: string;
      if (countOfValuesToImport === 0) {
        message = this.overwriteMessage.replace('%1', String(this.MAX_NUMBER_OF_SIGNALS));
        okButton = this.overWriteAllButton;
        cancelButton = this.cancelButton;
      } else {
        message = this.importDifOrOverwriteMessage.replace('%1', String(this.MAX_NUMBER_OF_SIGNALS)).replace('%2', String(countOfValuesToImport));
        okButton = this.overWriteAllButton;
        cancelButton = this.importDifButton.replace('%1', String(countOfValuesToImport));
      }

      const userConfirmed = await lastValueFrom(this.confirmationDialogService.handleConfirmationDialog(this.warningTitle, message, cancelButton, okButton));
      if (userConfirmed) {
        return numberOfNewValues;
      } else if (!userConfirmed && countOfValuesToImport !== 0) {
        return countOfValuesToImport;
      } else {
        return -1;
      }
    } else {
      return numberOfNewValues;
    }
  }

  private verifyIfSignalIndex(values: AdcValue[]) {
    const usedIndices = this.loadActualIndices();
    for (const value of values) {
      if (!value.index) {
        const message = this.noIndexMessage.replace('%s', value.name);
        this.confirmationDialogService.openConfirmationDialog(this.warningTitle, message, null, this.closeButton);
        return false;
      }
      if (usedIndices.length > 0 && usedIndices.includes(value.index)) {
        const message = this.duplicateIndexMessage.replace('%s', value.name);
        this.confirmationDialogService.openConfirmationDialog(this.warningTitle, message, null, this.closeButton);
        return false;
      }
      if (!this.allowedIndices.includes(value.index)) {
        const message = this.indexNotInRangeMessage.replace('%1', String(value.index)).replace('%2', value.name);
        this.confirmationDialogService.openConfirmationDialog(this.warningTitle, message, null, this.closeButton);
        return false;
      }
      usedIndices.push(value.index);
    }
    return true;
  }

  private updateAvailableIndices(values: AdcValue[]) {
    for (const value of values) {
      if (value.index !== undefined) {
        if (this.availableIndices.includes(value.index)) {
          this.availableIndices.splice(this.availableIndices.indexOf(value.index), 1);
        }
      }
    }
    this.updateIndices();
  }

  private loadActualIndices(): number[] {
    const usedIndices: number[] = [];
    for (const value of this.settings.values) {
      if (value.index !== undefined) {
        usedIndices.push(value.index);
      }
    }
    return usedIndices;
  }

  private updateSignalWithValidProperties(existingSignal: AdcValue | null, newSignal: AdcValue) {
    if (!existingSignal) {
      existingSignal = new AdcValue();
      existingSignal.name = newSignal.name;
    }
    existingSignal.ioType = this.getValidIoType(newSignal.ioType);
    existingSignal.index = newSignal.index;
    existingSignal.hysteresis = this.getValidHysteresisValue(newSignal.hysteresis);
    existingSignal.scaleMin = newSignal.scaleMin;
    existingSignal.scaleMax = newSignal.scaleMax;
    return existingSignal;
  }

  private getValidIoType(ioType: any) {
    if (!ioType) {
      return this.DEFAULT_IO_TYPE;
    }

    if (ioType !== this.DEFAULT_IO_TYPE && ioType !== this.IO_TYPE_DIGITAL) {
      return this.DEFAULT_IO_TYPE;
    }
    return ioType;
  }

  private getValidHysteresisValue(value) {
    if (!value) {
      return HYSTERESIS_RANGE_MIN;
    }

    const number = Number(value);
    if (Number.isNaN(number)) {
      return HYSTERESIS_RANGE_MIN;
    }

    if (number < HYSTERESIS_RANGE_MIN) {
      return HYSTERESIS_RANGE_MIN;
    }

    if (number > HYSTERESIS_RANGE_MAX) {
      return HYSTERESIS_RANGE_MAX;
    }

    return value;
  }
}
